import React, { useState } from 'react';
import { X, Clock, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useUpdateCustomEvent, useDeleteCustomEvent } from '../../api/schedules';
import { useFilterCamps } from '../../api';
import { useNotifications } from '../../context/NotificationContext';
import { useCampContext } from '../../context/CampContext';
import { formatDateToString } from '../../utils/dateUtils';
import CampSearchModal from './CampSearchModal';
import { Camp } from '../../utils/types';
import './CalendarEventPopup.css';

interface EventEditPopupProps {
  eventId: string | number;
  eventTitle: string;
  startDate: Date;
  endDate: Date;
  onClose: () => void;
  position: { top: number; left?: number; centered?: boolean };
  scheduleUuid: string | null;
}

const EventEditPopup: React.FC<EventEditPopupProps> = ({
  eventId,
  eventTitle,
  startDate,
  endDate,
  onClose,
  position,
  scheduleUuid
}) => {
  const [title, setTitle] = useState(eventTitle);
  const [hasChanges, setHasChanges] = useState(false);
  const [camps, setCamps] = useState<Camp[]>([]);
  const [showCampsPopup, setShowCampsPopup] = useState(false);
  const [selectedDatesByCampId, setSelectedDatesByCampId] = useState<Record<string, string>>({});

  const handleCampDateChange = (campId: string, dateId: string) => {
    setSelectedDatesByCampId(prev => ({ ...prev, [campId]: dateId }));
  };

  const updateCustomEventMutation = useUpdateCustomEvent();
  const deleteCustomEventMutation = useDeleteCustomEvent();
  const filterCampsMutation = useFilterCamps();
  const { showSuccess, showError } = useNotifications();
  const { schedule } = useCampContext();
  const navigate = useNavigate();

  const handleTitleChange = (newTitle: string) => {
    setTitle(newTitle);
    setHasChanges(newTitle !== eventTitle);
  };

  const handleSave = async () => {
    if (!title.trim()) {
      showError('Please enter a title for the event', new Error('Title is required'));
      return;
    }

    if (!scheduleUuid) {
      showError('No schedule selected', new Error('Schedule is required'));
      return;
    }

    const startDateStr = formatDateToString(startDate);
    const endDateStr = formatDateToString(endDate);

    updateCustomEventMutation.mutate(
      { scheduleUuid, eventId, title: title.trim(), startDate: startDateStr, endDate: endDateStr },
      {
        onSuccess: () => {
          showSuccess('Event updated successfully');
          onClose();
        },
        onError: (error) => {
          showError('Failed to update event. Please try again.', error);
        },
      }
    );
  };

  const handleDelete = async () => {
    if (!scheduleUuid) {
      showError('No schedule selected', new Error('Schedule is required'));
      return;
    }

    deleteCustomEventMutation.mutate(
      { scheduleUuid, eventId },
      {
        onSuccess: () => {
          showSuccess('Event deleted successfully');
          onClose();
        },
        onError: (error) => {
          showError('Failed to delete event. Please try again.', error);
        },
      }
    );
  };

  const handleSearchCamps = () => {
    const startDateStr = formatDateToString(startDate);
    const endDateStr = formatDateToString(endDate);

    filterCampsMutation.mutate(
      { start_date: startDateStr, end_date: endDateStr },
      {
        onSuccess: (data) => {
          const camps = data.camps || [];
          setCamps(camps);

          // Auto-select first date within range for each camp
          const initialSelections: Record<string, string> = {};

          camps.forEach(camp => {
            // Find first camp date that starts within the selected range (compare as strings to avoid timezone issues)
            const datesInRange = camp.camp_dates
              .filter(campDate => {
                return campDate.start_date >= startDateStr && campDate.start_date <= endDateStr;
              })
              .sort((a, b) => a.start_date.localeCompare(b.start_date));

            if (datesInRange.length > 0) {
              initialSelections[camp.id] = datesInRange[0].id;
            }
          });

          setSelectedDatesByCampId(initialSelections);
          setShowCampsPopup(true);
        },
        onError: (error) => {
          console.error('Failed to fetch camps:', error);
          setCamps([]);
          setShowCampsPopup(true);
        },
      }
    );
  };

  const getScheduledDateIds = (campId: string): string[] => {
    const scheduledDateIds: string[] = [];
    if (schedule?.scheduled_camps) {
      for (const scheduled of schedule.scheduled_camps) {
        if (scheduled.camp.id === campId) {
          scheduledDateIds.push(scheduled.camp_date.id);
        }
      }
    }
    return scheduledDateIds;
  };

  const handleOpenInSearch = () => {
    const startDateStr = formatDateToString(startDate);
    const endDateStr = formatDateToString(endDate);
    navigate(`/search?start_date=${startDateStr}&end_date=${endDateStr}`);
    onClose();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    }
  };

  // Generate selectedDates array from startDate and endDate
  const selectedDates: Date[] = [];
  const currentDate = new Date(startDate);
  const finalDate = new Date(endDate);

  while (currentDate <= finalDate) {
    selectedDates.push(new Date(currentDate));
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return (
    <div
      className={`event-edit-popup ${position.centered ? 'popup-centered' : ''}`}
      style={{
        top: `${position.top}px`,
        left: position.left !== undefined ? `${position.left}px` : undefined
      }}
    >
      <div className="calendar-popup-header">
        <button className="calendar-popup-close" onClick={onClose} aria-label="Close">
          <X className="icon icon--sm" />
        </button>
      </div>

      <div className="calendar-popup-content">
        <div className="calendar-popup-input-container">
          <input
            type="text"
            className="calendar-popup-title-input"
            placeholder="Event title"
            value={title}
            onChange={(e) => handleTitleChange(e.target.value)}
            onKeyDown={handleKeyPress}
            autoFocus
            maxLength={100}
          />
        </div>

        <div className="calendar-popup-date-display">
          <Clock className="calendar-popup-date-icon" size={16} />
          <span className="calendar-popup-date-text">
            {startDate.toDateString() === endDate.toDateString()
              ? startDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
              : `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} – ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
            }
          </span>
        </div>

        <button
          className="calendar-popup-search-link"
          onClick={handleSearchCamps}
          disabled={filterCampsMutation.isPending}
        >
          <Search className="calendar-popup-search-icon" size={16} />
          <span>
            {filterCampsMutation.isPending
              ? 'Loading...'
              : startDate.toDateString() === endDate.toDateString()
                ? 'Show camps for this date.'
                : 'Show camps for these dates.'
            }
          </span>
        </button>

        <div className="event-edit-actions">
          <button
            className="btn btn--danger btn--sm"
            onClick={handleDelete}
            disabled={updateCustomEventMutation.isPending || deleteCustomEventMutation.isPending}
          >
            {deleteCustomEventMutation.isPending ? 'Deleting...' : 'Delete'}
          </button>
          <button
            className="btn btn--primary btn--sm"
            onClick={handleSave}
            disabled={!hasChanges || !title.trim() || updateCustomEventMutation.isPending || deleteCustomEventMutation.isPending}
          >
            {updateCustomEventMutation.isPending ? 'Updating...' : 'Update'}
          </button>
        </div>
      </div>

      {showCampsPopup && (
        <CampSearchModal
          camps={camps}
          selectedDates={selectedDates}
          selectedDatesByCampId={selectedDatesByCampId}
          onDateChange={handleCampDateChange}
          getScheduledDateIds={getScheduledDateIds}
          onClose={() => setShowCampsPopup(false)}
          onOpenInSearch={handleOpenInSearch}
        />
      )}
    </div>
  );
};

export default EventEditPopup;
